/** `body.laneShip` — a ship locked to one horizontal lane (its spawn height):
 * left/right steering at a top speed (instant, or eased by an acceleration),
 * held inside the world by a side margin; a push (the push protocol: a
 * crosswind, a conveyor) carries it along the lane on top of its steering
 * (its `vx` only). Fixed shooters (the bottom-row
 * fighter), paddles and lane runners. */
import { bool, num, obj } from '../schemaBuilders.js';
import { defineModule, num as read } from '../module.js';
import { approach, clamp, pushVelocity } from '../physics.js';

export type LaneShipConfig = {
  speed: number;
  /** Units/s² towards the steered speed; 0 = instant. */
  accel: number;
  /** Extra clearance from each world edge beyond the half width. */
  margin: number;
  /** Mirror the sprite towards the steering direction (banking art). */
  faceMotion: boolean;
};

export const laneShip = defineModule<LaneShipConfig>({
  id: 'body.laneShip', version: 1, attach: 'entity', role: 'movement',
  summary: 'Lane-locked ship: steers left/right along its spawn height at a top speed (instant or eased), kept inside the world.',
  config: obj({ speed: num(0.5, 40), accel: num(0, 400), margin: num(0, 4), faceMotion: bool() }),
  requires: [], events: [], presentation: {}, proofs: ['lane-locked', 'instant-steer', 'eased-steer', 'edge-clamp', 'push-protocol', 'facing'],
  animStates: () => ['idle', 'move'],
  init(ctx, e) { ctx.data(e).laneY = e.y; },
  systems: {
    move(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e);
      const dx = e.slot !== null ? ctx.input(e.slot).dx : 0;
      const target = dx * c.speed;
      e.vx = c.accel === 0 ? target : approach(e.vx, target, c.accel * ctx.dt);
      e.vy = 0;
      const min = e.w / 2 + c.margin, max = ctx.world.bounds.w - e.w / 2 - c.margin;
      e.x = clamp(e.x + (e.vx + pushVelocity(ctx.signals('push'), e).vx) * ctx.dt, min, max);
      if ((e.x === min && e.vx < 0) || (e.x === max && e.vx > 0)) e.vx = 0;
      e.y = read(d, 'laneY');
      if (c.faceMotion && dx !== 0) e.facing = dx;
    },
    present(ctx, e) {
      if (e && e.status === 'active') ctx.setAnim(e, Math.abs(e.vx) > 0.05 ? 'move' : 'idle');
    },
  },
});
